import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SiteContentService, SiteContent } from '../services/SiteContentService';

export const PrivacyPolicy: React.FC = () => {
  const [content, setContent] = useState<SiteContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPrivacyPolicy();
  }, []);

  const loadPrivacyPolicy = async () => {
    try {
      const data = await SiteContentService.getContentByKey('privacy_policy');
      setContent(data);
    } catch (error) {
      console.error('Error loading privacy policy:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 to-[#1a0530] text-white">
      <header className="bg-purple-950/80 backdrop-blur-sm border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                src="/TSL_Logo.png"
                alt="The Singing League Logo"
                className="h-8 w-8 mr-2 object-contain"
              />
              <span className="font-bold text-lg">The Singing League</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/help" className="px-4 py-2 rounded-md text-white/80 hover:text-white transition-colors">
                Help
              </Link>
              <Link to="/login" className="px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors">
                Log In
              </Link>
              <Link to="/signup" className="px-4 py-2 rounded-md bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-500/20 rounded-full mb-4">
            <Shield size={32} className="text-violet-400" />
          </div>
          <h1 className="text-5xl font-bold mb-4">{content?.title || 'Privacy Policy'}</h1>
          {content && (
            <p className="text-xl text-white/70">
              Last Updated: {new Date(content.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>

        {loading ? (
          <div className="bg-purple-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-8">
            <div className="text-center text-white/60">Loading...</div>
          </div>
        ) : content ? (
          <div className="bg-purple-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-8">
            <div className="prose prose-invert prose-lg max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h2 className="text-3xl font-bold mb-4 text-white">{children}</h2>,
                  h2: ({ children }) => <h3 className="text-2xl font-bold mb-4 mt-8 text-white">{children}</h3>,
                  h3: ({ children }) => <h4 className="text-xl font-bold mb-3 mt-6 text-white">{children}</h4>,
                  p: ({ children }) => <p className="text-white/80 leading-relaxed mb-4">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside text-white/80 mb-4 space-y-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside text-white/80 mb-4 space-y-2">{children}</ol>,
                  li: ({ children }) => <li className="ml-4">{children}</li>,
                  a: ({ href, children }) => (
                    <a href={href} className="text-violet-400 hover:underline">
                      {children}
                    </a>
                  ),
                }}
              >
                {content.content}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="bg-purple-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-8">
            <div className="text-center text-white/60">Privacy policy not found</div>
          </div>
        )}

        <div className="mt-12 text-center">
          <div className="bg-purple-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <p className="text-white/70 mb-4">
              Have questions about our privacy practices?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@digitraxmail.com"
                className="px-6 py-3 bg-violet-600 hover:bg-violet-700 rounded-lg font-medium transition-colors"
              >
                Contact Us
              </a>
              <Link
                to="/help"
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
              >
                Help Center
              </Link>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-white/10 mt-16 py-8">
        <div className="container mx-auto px-4 text-center text-white/60 text-sm">
          <p className="mb-2">&copy; {new Date().getFullYear()} DigiTrax Entertainment, Inc. All rights reserved.</p>
          <div className="flex justify-center space-x-4">
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <span>|</span>
            <Link to="/data-deletion" className="hover:text-white transition-colors">
              Data Deletion
            </Link>
            <span>|</span>
            <Link to="/help" className="hover:text-white transition-colors">
              Help & Support
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
